import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import type { AdScreenSlotName } from '@anon-chat/shared';
import { useAdsStore, useScreenSlotCode, useSideSlotCode } from '@/store/ads.store';

/**
 * Сколько ждать объявления, прежде чем счесть, что его не будет.
 *
 * Ожидание щедрое, потому что незаметное: пока объявление не нарисовано, баннер
 * не показан и нажатия не перехватывает. У сетей бывает своя задержка показа,
 * заданная в кабинете, — торопиться незачем.
 */
const DRAW_WAIT_MS = 10_000;
/** Ниже этой высоты считаем, что сеть ничего не нарисовала. */
const MIN_AD_HEIGHT_PX = 40;

/**
 * Рекламное место на экране.
 *
 * Рекламные сети выдают два куска кода: загрузчик — в `<head>`, и блок — туда,
 * где реклама должна появиться. Второй нельзя поставить перед `</body>`:
 * приложение занимает экран целиком, и вставка оказывается за его пределами,
 * где её никто не увидит. Поэтому места объявлены прямо на экранах, а их
 * содержимое приходит из панели — своё для компьютера и для телефона.
 *
 * Пустое место не рисует ничего — ни отступа, ни рамки.
 */
export function AdSlot({
  name,
  className,
}: {
  name: AdScreenSlotName;
  className?: string;
}): React.ReactNode {
  const code = useScreenSlotCode(name);
  return <AdHost code={code} className={className} slot={name} />;
}

/**
 * Боковые рекламные места веб-версии.
 *
 * На широком экране колонка приложения занимает середину, а по краям остаётся
 * пустое поле — там и место объявлениям. Полосы стоят фиксированно и не
 * участвуют в потоке: иначе заполненная одна из них сдвинула бы колонку с
 * середины. Ниже 1280 px свободного места нет, и полосы не показываются вовсе —
 * объявление либо налезло бы на приложение, либо ужалось до нечитаемого.
 *
 * На каких экранах они видны, решает панель: в диалоге реклама рядом с живым
 * разговором мешает сильнее всего, поэтому по умолчанию там её нет.
 */
export function AdRails(): React.ReactNode {
  const left = useSideSlotCode('sideLeft');
  const right = useSideSlotCode('sideRight');

  return (
    <>
      <div className="fixed left-4 top-1/2 hidden -translate-y-1/2 xl:block">
        <AdHost code={left} slot="sideLeft" className="w-[240px] 2xl:w-[300px]" />
      </div>
      <div className="fixed right-4 top-1/2 hidden -translate-y-1/2 xl:block">
        <AdHost code={right} slot="sideRight" className="w-[240px] 2xl:w-[300px]" />
      </div>
    </>
  );
}

/**
 * Всплывающий баннер.
 *
 * Появляется только там, где человек сам нажал кнопку и готов к паузе: после
 * завершённого им диалога и перед началом поиска. Посреди разговора — никогда,
 * и никогда поверх сообщения о том, что собеседник вышел.
 *
 * Затемнение включается не в момент вызова, а когда сеть действительно
 * нарисовала объявление. Причин две. Сеть может не нарисовать ничего —
 * объявления кончились, блок не прошёл модерацию, — и тогда человек упирался бы
 * в пустое затемнение на ровном месте. А может нарисовать с задержкой, заданной
 * в её кабинете, или вовсе своим окном поверх страницы: полноэкранные форматы
 * так и работают, и наша рамка им не нужна.
 *
 * Поэтому код исполняется сразу, но в невидимом слое, который не перехватывает
 * нажатия. Появилось объявление — показываем рамку с кнопкой; не появилось за
 * отведённое время — тихо убираем, человек ничего не заметил.
 */
export function AdInterstitial(): React.ReactNode {
  const code = useAdsStore((state) => state.openInterstitial);
  const close = useAdsStore((state) => state.closeInterstitial);
  const markEmpty = useAdsStore((state) => state.markInterstitialEmpty);
  const hostRef = useRef<HTMLDivElement>(null);
  const [drawn, setDrawn] = useState(false);

  useEffect(() => {
    setDrawn(false);

    const host = hostRef.current;
    if (!code || !host) {
      return;
    }

    const drawnNow = () => host.getBoundingClientRect().height >= MIN_AD_HEIGHT_PX;

    // Размер меняет сама сеть, когда вставляет объявление: наблюдатель ловит
    // это и через секунду, и через десять — сколько бы ни заняла её задержка.
    const observer = new ResizeObserver(() => {
      if (drawnNow()) {
        setDrawn(true);
      }
    });
    observer.observe(host);

    const timer = window.setTimeout(() => {
      if (!drawnNow()) {
        markEmpty();
      }
    }, DRAW_WAIT_MS);

    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, [code, markEmpty]);

  // Клавиша Escape закрывает: показанный баннер перекрывает приложение целиком.
  useEffect(() => {
    if (!code || !drawn) {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [code, drawn, close]);

  if (!code) {
    return null;
  }

  return (
    <div
      className={clsx(
        'fixed inset-0 z-[60] flex items-center justify-center p-4 transition-opacity duration-200',
        drawn ? 'bg-black/70' : 'pointer-events-none opacity-0',
      )}
      onClick={drawn ? close : undefined}
    >
      <div
        className="flex max-h-full w-full max-w-[640px] flex-col items-end gap-2"
        onClick={(event) => event.stopPropagation()}
      >
        {/*
          Кнопка над объявлением, а не поверх него: она не должна зависеть от
          того, какой высоты блок нарисовала сеть, и не должна закрывать собой
          его угол. Работает сразу — баннер, который нельзя закрыть, человек
          закрывает вместе со вкладкой.
        */}
        {drawn ? (
          <button
            type="button"
            onClick={close}
            className="flex items-center gap-1.5 rounded-full bg-surface px-3.5 py-2
                       text-[14px] text-text active:bg-surface-raised"
          >
            Закрыть
            <svg viewBox="0 0 24 24" className="size-4" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        ) : null}

        <div className={clsx('w-full overflow-auto rounded-2xl', drawn && 'bg-surface p-3')}>
          <AdHost code={code} slot="interstitial" hostRef={hostRef} />
        </div>
      </div>
    </div>
  );
}

/**
 * Вместилище чужой разметки.
 *
 * Ширина ограничена: блок, свёрстанный под другой экран, иначе растягивает
 * страницу и ломает разметку — с этим и столкнулись на телефоне.
 */
function AdHost({
  code,
  slot,
  className,
  hostRef,
}: {
  code: string | null;
  slot: string;
  className?: string;
  hostRef?: React.RefObject<HTMLDivElement | null>;
}): React.ReactNode {
  const ownRef = useRef<HTMLDivElement>(null);
  const ref = hostRef ?? ownRef;

  useEffect(() => {
    const host = ref.current;
    if (!host || !code) {
      return;
    }

    injectHtml(host, code);
    return () => host.replaceChildren();
  }, [code, ref]);

  if (!code) {
    return null;
  }

  return (
    <div
      ref={ref}
      data-ad-slot={slot}
      className={className}
      style={{ maxWidth: '100%', overflowX: 'hidden' }}
    />
  );
}

/**
 * Вставка чужой разметки со скриптами.
 *
 * Через innerHTML скрипты не исполняются — браузер намеренно их игнорирует, а
 * код рекламных сетей ровно из скрипта рядом с контейнером и состоит. Поэтому
 * разметка разбирается, а каждый скрипт пересоздаётся: только у созданного
 * через createElement узла браузер запускает содержимое.
 */
function injectHtml(host: HTMLElement, html: string): void {
  host.innerHTML = html;

  // Обход всего поддерева, а не только верхнего уровня: сети заворачивают блок
  // в свой контейнер вместе со скриптом.
  for (const stale of Array.from(host.querySelectorAll('script'))) {
    stale.replaceWith(reviveScript(stale));
  }
}

function reviveScript(source: HTMLScriptElement): HTMLScriptElement {
  const script = document.createElement('script');
  for (const attribute of Array.from(source.attributes)) {
    script.setAttribute(attribute.name, attribute.value);
  }
  script.text = source.text;
  return script;
}
